import iconUrl from "url:../assets/branding/icon.png";
import "./splash.css";

// Phase timings (ms)
const SPLASH_FADE_IN_MS = 1000; // 1 — splash fades in
const WORLD_REVEAL_DELAY_MS = 2000; // 2 — delay after world ready before revealing
const WORLD_REVEAL_MS = 1000; // 3 — world fades in behind splash
const HOLD_MS = 2000; // 4 — hold before auto-close
const CLOSE_FADE_MS = 1000; // 5 — splash fades out

/**
 * @param {Promise<void>} readyPromise  resolves when the world has finished loading
 */
export function mountSplashScreen(readyPromise) {
  const reduceMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;

  const overlay = document.createElement("div");
  overlay.id = "splash-overlay";
  if (reduceMotion) overlay.classList.add("no-motion");

  overlay.innerHTML = `
    <div id="splash-card">
      <img id="splash-icon" src="${iconUrl}" alt="Hexaworlds icon" />
      <div id="splash-wordmark">Hexaworlds</div>
      <div id="splash-tagline">Build your world, at your pace.</div>
      <div id="splash-hint">Click anywhere to start</div>
    </div>
  `;

  document.body.appendChild(overlay);
  // splash-active sets up the canvas transition BEFORE any blur is applied
  document.body.classList.add("splash-active");

  // Overlay is immediately opaque (hides the world).
  // Phase 1 — fade the card content in.
  const card = overlay.querySelector("#splash-card");
  if (!reduceMotion) {
    requestAnimationFrame(() =>
      requestAnimationFrame(() => card.classList.add("visible"))
    );
  } else {
    card.classList.add("visible");
  }

  let closed = false;
  let autoCloseTimer = null;
  let worldRevealTimer = null;
  let holdTimer = null;

  function close() {
    if (closed) return;
    closed = true;

    overlay.removeEventListener("pointerdown", onPointer);
    document.removeEventListener("keydown", onKey);
    clearTimeout(worldRevealTimer);
    clearTimeout(holdTimer);
    clearTimeout(autoCloseTimer);

    if (reduceMotion) {
      document.body.classList.remove("splash-active", "splash-world-visible");
      overlay.remove();
      return;
    }

    // Start blur fade-out simultaneously with overlay fade-out
    document.body.classList.remove("splash-world-visible");
    overlay.classList.add("hiding");

    function onTransitionEnd(e) {
      if (e.propertyName === "opacity") {
        overlay.removeEventListener("transitionend", onTransitionEnd);
        document.body.classList.remove("splash-active");
        overlay.remove();
      }
    }
    overlay.addEventListener("transitionend", onTransitionEnd);
  }

  // Phase 2–4 — triggered when world is ready
  readyPromise.then(() => {
    worldRevealTimer = setTimeout(() => {
      if (closed) return;

      // Phase 3 — reveal world behind splash
      overlay.classList.add("world-visible");
      document.body.classList.add("splash-world-visible");

      // Phase 4 — hold, then auto-close
      holdTimer = setTimeout(
        () => {
          if (closed) return;
          autoCloseTimer = setTimeout(close, HOLD_MS);
        },
        reduceMotion ? 0 : WORLD_REVEAL_MS
      );
    }, WORLD_REVEAL_DELAY_MS);
  });

  function onPointer(e) {
    e.stopPropagation();
    close();
  }

  function onKey(e) {
    if (e.key === "Escape") close();
  }

  overlay.addEventListener("pointerdown", onPointer);
  document.addEventListener("keydown", onKey);
}
